import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import bgImage from '../../assets/images/background/bg-7.jpg';

// Interface definitions
interface User {
  _id: string;
  name: string;
  email: string;
}

interface AuthService {
  register(userData: { name: string; email: string; password: string }): Promise<{ token: string; user: User }>;
  login(credentials: { email: string; password: string }): Promise<{ token: string; user: User }>;
  logout(): void;
}

interface SavedWorkout {
  id: string;
  discipline: string;
  workoutStyle: string;
  date: string;
  content: string;
  user?: string;
}

interface Program {
  _id: string;
  discipline: string;
  level: string;
  focusArea: string;
  content: string;
  purchaseDate: string;
  stripeSessionId: string;
}

interface Notification {
  type: 'success' | 'error' | 'info';
  message: string;
}

// API service definitions
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://what-workout25-c959.vercel.app';

const authService: AuthService = {
  async register(userData) {
    try {
      console.log('Attempting to register with:', userData.email);
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      // Get response text first to check if it's valid JSON
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', responseText);
        throw new Error('Server returned invalid JSON');
      }
      
      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }
      
      // Store authentication data
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },
  
  async login(credentials) {
    try {
      console.log('Attempting to login with:', credentials.email);
      const response = await fetch(`${API_BASE_URL}/api/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });
      
      // Get response text first to check if it's valid JSON
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', responseText);
        throw new Error('Server returned invalid JSON');
      }
      
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      
      // Store authentication data
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
};

const workoutService = {
  async getWorkouts(): Promise<SavedWorkout[]> {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token');
      
      const response = await fetch(`${API_BASE_URL}/api/workouts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Failed to fetch workouts');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching workouts:', error);
      throw error;
    }
  },
  
  async deleteWorkout(id: string): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token');
      
      const response = await fetch(`${API_BASE_URL}/api/workouts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Failed to delete workout');
      }
    } catch (error) {
      console.error('Error deleting workout:', error);
      throw error;
    }
  },
  
  async createWorkout(workout: Omit<SavedWorkout, 'id'>): Promise<SavedWorkout> {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token');
      
      const response = await fetch(`${API_BASE_URL}/api/workouts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workout),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Failed to create workout');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating workout:', error);
      throw error;
    }
  }
};

const programService = {
  async getUserPrograms(): Promise<Program[]> {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token');
      
      const response = await fetch(`${API_BASE_URL}/api/payment/user-programs`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Failed to fetch programs');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching programs:', error);
      throw error;
    }
  },
  
  async verifySession(sessionId: string): Promise<any> {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token');
      
      const response = await fetch(`${API_BASE_URL}/api/payment/verify-session/${sessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Failed to verify session');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error verifying session:', error);
      throw error;
    }
  }
};

interface ProfilePageProps {
  navigateTo?: (path: string) => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ navigateTo }) => {
  const navigate = useNavigate();
  const [savedWorkouts, setSavedWorkouts] = useState<SavedWorkout[]>([]);
  const [userPrograms, setUserPrograms] = useState<Program[]>([]);
  const [viewingWorkout, setViewingWorkout] = useState<SavedWorkout | null>(null);
  const [viewingProgram, setViewingProgram] = useState<Program | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [isSignup, setIsSignup] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [notification, setNotification] = useState<Notification | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  // Styles
  // Styles
  const buttonStyle = {
    padding: '15px 30px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    textDecoration: 'none',
    textTransform: 'uppercase' as const,
    letterSpacing: '3px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '0',
    transition: 'all 0.3s ease',
    margin: '5px',
    display: 'inline-block',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 15px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontSize: '14px',
    marginBottom: '15px',
    fontFamily: "'Montserrat', sans-serif",
  };
  
  // Check for mobile viewport
useEffect(() => {
  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };
  
  // Initial check
  checkMobile();
  
  // Add event listener for window resize
  window.addEventListener('resize', checkMobile);
  
  // Cleanup
  return () => window.removeEventListener('resize', checkMobile);
}, []);

  // Check for logged in user and pending program session
  useEffect(() => {
    const checkAuthAndPendingProgram = async () => {
      const token = localStorage.getItem('token');
      const userJson = localStorage.getItem('user');
      
      if (token && userJson) {
        try {
          const userData = JSON.parse(userJson);
          setIsLoggedIn(true);
          setLoading(true);
          setEmail(userData.email);
          setName(userData.name || '');
          
          // Check for pending program session ID
          const pendingSessionId = localStorage.getItem('pendingProgramSessionId');
          if (pendingSessionId) {
            console.log('Found pending session ID:', pendingSessionId);
            
            try {
              const result = await programService.verifySession(pendingSessionId);
              if (result.success) {
                setNotification({
                  type: 'success',
                  message: 'Your program has been added to your account!'
                });
              }
            } catch (error) {
              console.error('Failed to verify pending session:', error);
            } finally {
              localStorage.removeItem('pendingProgramSessionId');
            }
          }
          
          // Load user data regardless of pending session
          await loadUserData();
        } catch (error) {
          console.error('Error during auth check:', error);
          setLoading(false);
        }
      }
    };
    
    checkAuthAndPendingProgram();
  }, []);

  // Load user data (workouts and programs)
  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Sequential loading to avoid overwhelming the server
      try {
        const workouts = await workoutService.getWorkouts();
        setSavedWorkouts(workouts);
      } catch (workoutError) {
        console.error('Failed to load workouts:', workoutError);
      }
      
      try {
        const programs = await programService.getUserPrograms();
        setUserPrograms(programs);
      } catch (programError) {
        console.error('Failed to load programs:', programError);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setErrorMessage('Failed to load your data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle authentication (login/signup)
  const handleAuthentication = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);
    
    try {
      if (isSignup) {
        await authService.register({ name, email, password });
      } else {
        await authService.login({ email, password });
      }
      
      setIsLoggedIn(true);
      
      // Check for pending program ID after login
      const pendingSessionId = localStorage.getItem('pendingProgramSessionId');
      if (pendingSessionId) {
        try {
          const result = await programService.verifySession(pendingSessionId);
          if (result.success) {
            setNotification({
              type: 'success',
              message: 'Your program has been added to your account!'
            });
          }
        } catch (error) {
          console.error('Error processing pending program:', error);
        } finally {
          localStorage.removeItem('pendingProgramSessionId');
        }
      }
      
      // Load user data
      await loadUserData();
    } catch (error: any) {
      console.error('Authentication error:', error);
      setErrorMessage(error.message || 'Authentication failed. Please check your credentials.');
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    authService.logout();
    setIsLoggedIn(false);
    setEmail('');
    setPassword('');
    setName('');
    setSavedWorkouts([]);
    setUserPrograms([]);
    navigate('/');
  };

  // Navigate back
  const handleBack = () => {
    navigate('/');
  };

  // View workout details
  const handleViewWorkout = (workout: SavedWorkout) => {
    setViewingWorkout(workout);
    setViewingProgram(null);
    setTimeout(() => {
      const viewerElement = document.getElementById('content-viewer');
      if (viewerElement) {
        viewerElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  // View program details
  const handleViewProgram = (program: Program) => {
    setViewingProgram(program);
    setViewingWorkout(null);
    setTimeout(() => {
      const viewerElement = document.getElementById('content-viewer');
      if (viewerElement) {
        viewerElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Delete workout
  const handleDeleteWorkout = async (id: string) => {
    try {
      await workoutService.deleteWorkout(id);
      setSavedWorkouts(savedWorkouts.filter(workout => workout.id !== id));
    } catch (error) {
      console.error('Error deleting workout:', error);
      setErrorMessage('Failed to delete workout');
    }
  };

  return (
    <React.Fragment>
      <button
  onClick={handleBack}
  style={{
    ...buttonStyle,
    position: 'absolute' as const,
    top: isMobile ? '10px' : '20px',
    left: isMobile ? '10px' : '20px',
    zIndex: 10,
    padding: isMobile ? '8px 15px' : '10px 20px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  }}
>
  BACK
</button>
      
      <section
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          minHeight: '100vh',
          color: 'white',
          position: 'relative',
        }}
      >
        <div style={{ 
          position: 'absolute', 
          top: '0', 
          left: '0', 
          width: '100%', 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)', 
          padding: '0 20px',
        }}>
          {notification && (
            <div style={{
              backgroundColor: notification.type === 'success' 
                ? 'rgba(40, 167, 69, 0.7)' 
                : 'rgba(220, 53, 69, 0.7)',
              color: 'white',
              padding: '15px',
              marginBottom: '20px',
              borderRadius: '4px',
              maxWidth: '600px',
              width: '100%',
              textAlign: 'center',
              position: 'absolute',
              top: '20px',
              zIndex: 100,
            }}>
              {notification.message}
              <button 
                onClick={() => setNotification(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  position: 'absolute',
                  right: '10px',
                  top: '15px',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>
          )}
          
          {errorMessage && (
            <div style={{
              backgroundColor: 'rgba(220, 53, 69, 0.7)',
              color: 'white',
              padding: '15px',
              marginBottom: '20px',
              borderRadius: '4px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center'
            }}>
              {errorMessage}
            </div>
          )}

          {!isLoggedIn ? (
            <div style={{ 
              maxWidth: '400px', 
              width: '100%', 
              padding: '30px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <h2 style={{
                fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                fontWeight: '700',
                fontSize: '36px',
                textTransform: 'uppercase',
                letterSpacing: '5px',
                color: '#ffffff',
                marginBottom: '20px',
                textAlign: 'center',
              }}>
                {isSignup ? 'CREATE ACCOUNT' : 'LOGIN'}
              </h2>
              
              <form onSubmit={handleAuthentication}>
                {isSignup && (
                  <input 
                    type="text" 
                    placeholder="Name" 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    style={inputStyle}
                    required
                  />
                )}
                
                <input 
                  type="email" 
                  placeholder="Email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  style={inputStyle}
                  required
                />
                
                <input 
                  type="password" 
                  placeholder="Password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  style={inputStyle}
                  required
                />
                
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...buttonStyle,
                    width: '100%',
                    backgroundColor: loading ? 'rgba(150, 150, 150, 0.25)' : 'rgba(255, 255, 255, 0.25)',
                    margin: '10px 0 20px 0',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'PROCESSING...' : (isSignup ? 'SIGN UP' : 'LOGIN')}
                </button>
                {!isSignup && (
  <div style={{ textAlign: 'center', marginTop: '10px' }}>
    <button
      onClick={(e) => {
        e.preventDefault();
        navigate('/forgot-password');
      }}
      style={{
        background: 'none',
        border: 'none',
        color: 'rgba(255, 255, 255, 0.7)',
        cursor: 'pointer',
        fontSize: '14px',
        textDecoration: 'underline',
      }}
    >
      Forgot your password?
    </button>
  </div>
)}
              </form>
              
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => setIsSignup(!isSignup)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.7)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textDecoration: 'underline',
                  }}
                >
                  {isSignup ? 'Already have an account? Login' : 'Need an account? Sign up'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ 
              maxWidth: '800px', 
              width: '100%', 
              padding: '0 15px',
            }}>
              <h1 style={{
                fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                fontWeight: '700',
                fontSize: '60px',
                textTransform: 'uppercase',
                letterSpacing: '10px',
                color: '#ffffff',
                marginBottom: '40px',
                textAlign: 'center',
                marginTop: notification ? '60px' : '0',
              }}>
                MY PROFILE
              </h1>
              
              {/* Programs Section */}
              <div style={{ 
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: '30px',
                marginBottom: '30px',
              }}>
                <h2 style={{
                  fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                  fontWeight: '700',
                  fontSize: '28px',
                  textTransform: 'uppercase',
                  letterSpacing: '3px',
                  color: '#ffffff',
                  marginBottom: '20px',
                }}>
                  MY TRAINING PROGRAMS
                </h2>
                
                {loading ? (
                  <p style={{ textAlign: 'center', marginTop: '20px' }}>
                    Loading your programs...
                  </p>
                ) : userPrograms.length === 0 ? (
                  <p style={{ textAlign: 'center', marginTop: '20px' }}>
                    You don't have any purchased programs yet.
                  </p>
                ) : (
                  <div>
                    {userPrograms.map((program) => (
                      <div key={program._id} style={{
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '20px',
                        marginBottom: '15px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                          <div>
                            <h3 style={{
                              fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                              fontWeight: '600',
                              fontSize: '22px',
                              marginBottom: '5px',
                            }}>
                              {program.level} {program.discipline} Program
                            </h3>
                            <p style={{ 
                              fontSize: '14px', 
                              color: 'rgba(255, 255, 255, 0.7)',
                            }}>
                              Focus: {program.focusArea || 'General'}
                            </p>
                            <p style={{ 
                              fontSize: '14px', 
                              color: 'rgba(255, 255, 255, 0.7)',
                            }}>
                              Purchased: {new Date(program.purchaseDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <button
                              onClick={() => handleViewProgram(program)}
                              style={{
                                ...buttonStyle,
                                padding: '8px 15px',
                                margin: '0 5px 0 0',
                                backgroundColor: 'rgba(40, 167, 69, 0.3)',
                                borderColor: 'rgba(40, 167, 69, 0.5)',
                              }}
                            >
                              VIEW PROGRAM
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Workouts Section */}
              <div style={{ 
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: '30px',
                marginBottom: '30px',
              }}>
                <h2 style={{
                  fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                  fontWeight: '700',
                  fontSize: '28px',
                  textTransform: 'uppercase',
                  letterSpacing: '3px',
                  color: '#ffffff',
                  marginBottom: '20px',
                }}>
                  SAVED WORKOUTS
                </h2>
                
                {loading ? (
                  <p style={{ textAlign: 'center', marginTop: '20px' }}>
                    Loading your workouts...
                  </p>
                ) : savedWorkouts.length === 0 ? (
                  <p style={{ textAlign: 'center', marginTop: '20px' }}>
                    You don't have any saved workouts yet.
                  </p>
                ) : (
                  <div>
                    {savedWorkouts.map((workout) => (
                      <div key={workout.id} style={{
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '20px',
                        marginBottom: '15px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                          <div>
                            <h3 style={{
                              fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                              fontWeight: '600',
                              fontSize: '22px',
                              marginBottom: '5px',
                            }}>
                              {workout.discipline} - {workout.workoutStyle}
                            </h3>
                            <p style={{ 
                              fontSize: '14px', 
                              color: 'rgba(255, 255, 255, 0.7)',
                            }}>
                              Saved on: {new Date(workout.date).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <button
                              onClick={() => handleViewWorkout(workout)}
                              style={{
                                ...buttonStyle,
                                padding: '8px 15px',
                                margin: '0 5px 0 0',
                              }}
                            >
                              VIEW
                            </button>
                            <button
                              onClick={() => handleDeleteWorkout(workout.id)}
                              style={{
                                ...buttonStyle,
                                padding: '8px 15px',
                                margin: '0',
                                backgroundColor: 'rgba(220, 53, 69, 0.3)',
                                borderColor: 'rgba(220, 53, 69, 0.5)',
                              }}
                            >
                              DELETE
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Content Viewer - Used for both workouts and programs */}
              {(viewingWorkout || viewingProgram) && (
                <div 
                  id="content-viewer" 
                  style={{ 
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    padding: '30px',
                    marginBottom: '30px',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                  }}>
                    <h2 style={{
                      fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                      fontWeight: '700',
                      fontSize: '28px',
                      textTransform: 'uppercase',
                      letterSpacing: '3px',
                      color: '#ffffff',
                      margin: 0,
                    }}>
                      {viewingWorkout 
                        ? `${viewingWorkout.discipline} - ${viewingWorkout.workoutStyle}` 
                        : `${viewingProgram?.level} ${viewingProgram?.discipline} Program`}
                    </h2>
                    <button
                      onClick={() => {
                        setViewingWorkout(null);
                        setViewingProgram(null);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        fontSize: '20px',
                        cursor: 'pointer',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div style={{
                    backgroundColor: 'white',
                    color: 'black',
                    padding: '20px',
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: "'Montserrat', sans-serif",
                    maxHeight: '500px',
                    overflowY: 'auto',
                  }}>
                    {viewingWorkout 
                      ? viewingWorkout.content 
                      : viewingProgram?.content}
                  </div>
                </div>
              )}
              
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => window.location.href = '/training-plan'}
                  style={{
                    ...buttonStyle,
                    backgroundColor: 'rgba(40, 167, 69, 0.3)',
                    borderColor: 'rgba(40, 167, 69, 0.5)',
                  }}
                >
                  BROWSE TRAINING PLANS
                </button>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    ...buttonStyle,
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  }}
                >
                  EXPLORE WORKOUTS
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    ...buttonStyle,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  LOGOUT
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </React.Fragment>
  );
};

export default ProfilePage;